import { useState, useEffect, useMemo, useRef } from "react";
import {
  fetchFinalSubbies,
  fetchFinalSubbieOptions,
  updateFinalSubbie,
} from "../utils/api.js";
import { ErrorIcon } from "../components/shared/Icons";
import SubcontractorModal, { onboardingBucket } from "../components/SubcontractorModal";
import AddSubbieModal from "../components/AddSubbieModal";
import SubbieEditorModal from "../components/SubbieEditorModal";
import ActivatedModal from "../components/ActivatedModal";
import LoadingState from "../components/shared/LoadingState";
import SearchInput from "../components/shared/SearchInput";
import CustomSelect from "../components/shared/CustomSelect";
import HelpButton from "../components/shared/HelpButton";
import { cssUrl } from "../utils/cssVersion";

const personalStyles = cssUrl("newuseronboarding.css");

/* ============================================================================
 * SubcontractorComplete
 * ----------------------------------------------------------------------------
 * Final-onboarding screen for subcontractors (Airtable "final subbie" table).
 * Everything is read-only except:
 *   • Subbie          – linked field, add from autocomplete (partner Subbie table)
 *   • Active Clients  – linked field, add from autocomplete (Main_Sites view)
 *   • Position        – single select
 * Edits auto-save on change, the same way UsersManagement does.
 * ==========================================================================*/

const FIELD_LABELS = {
  subbie: "Subbie",
  activeClients: "Active Clients",
  position: "Position",
  name: "Name",
  mobile: "Mobile",
  abnNumber: "ABN Number",
  email: "Email",
  firstName: "First Name",
  surname: "Surname",
  dateOfBirth: "Date Of Birth",
  working: "Working",
};
const labelFor = (field) => FIELD_LABELS[field] ?? field;

// Fallback only — the live list comes from /api/subcontractor/final/options
const POSITION_OPTIONS_FALLBACK = [
  "Subcontractor",
  "Sales",
  "Accounts",
  "Operations Manager",
  "Supervisor",
  "Manager",
  "IT Manager",
  "Accountant",
];

// "2000-12-12" or "2000-12-12T00:00:00.000Z" -> "12/12/2000"
const toDisplayDate = (v) => {
  const s = (v ?? "").toString().trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

const normalizeName = (name) =>
  (name ?? "").toString().toLowerCase().replace(/\s+/g, "");

const sameValue = (a, b) => JSON.stringify(a ?? "") === JSON.stringify(b ?? "");

/* ============================================================================
 * Icons
 * ==========================================================================*/

// Users icon with a plus — "Subcontractor Final Onboarding"
const UsersPlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8" cy="7" r="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 8v6M22 11h-6" />
  </svg>
);

// Users icon with a pencil — "Subbie Editor"
const UsersEditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8" cy="7" r="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.5 3.5a1.9 1.9 0 0 1 2.7 2.7L16 13.5l-3.5.8.8-3.5 7.2-7.3z" />
  </svg>
);

// User with a check badge — "Activated"
const UserCheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 11l2 2 4-4" />
  </svg>
);

const Spin = () => (
  <span
    style={{
      width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent",
      borderRadius: "50%", display: "inline-block", animation: "sc-spin 0.7s linear infinite",
    }}
  />
);

const IconButton = ({ onClick, alertCount, title, icon }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    style={{
      position: "relative",
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      border: "1px solid rgba(34, 211, 238, 0.25)",
      background: "rgba(34, 211, 238, 0.08)",
      color: "#22d3ee",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
      flexShrink: 0,
    }}
  >
    {icon}
    {alertCount > 0 && (
      <span
        style={{
          position: "absolute",
          top: "-5px",
          right: "-5px",
          minWidth: "16px",
          height: "16px",
          borderRadius: "999px",
          background: "#dc2626",
          color: "#fff",
          fontSize: "0.6rem",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 4px",
        }}
      >
        {alertCount}
      </span>
    )}
  </button>
);

/* ============================================================================
 * Saving wrapper — blurs + locks a field while its save is in flight
 * ==========================================================================*/

const SavingWrap = ({ saving, children, style }) => (
  <div
    style={{
      minWidth: 0,
      filter: saving ? "blur(2px)" : "none",
      opacity: saving ? 0.5 : 1,
      pointerEvents: saving ? "none" : "auto",
      transition: "filter 0.15s ease, opacity 0.15s ease",
      ...style,
    }}
  >
    {children}
  </div>
);

/* ============================================================================
 * ReadOnlyField
 * ==========================================================================*/

const ReadOnlyField = ({ label, value }) => (
  <div className="ob-field" style={{ minWidth: 0 }}>
    <label className="ob-label">{label}</label>
    <input
      type="text"
      value={value ?? ""}
      readOnly
      className="ob-input ob-input-readonly"
      title={(value ?? "").toString()}
    />
  </div>
);

/* ============================================================================
 * LinkedAutocomplete — chips + type-ahead over { id, name } items. Values can
 * ONLY be added from the option list (linked record fields). Saves on every
 * add / remove. If `onAddNew` is given, the no-match state offers a button
 * to create a new entry instead.
 * ==========================================================================*/

const LinkedAutocomplete = ({
  label,
  values,
  options,
  saving,
  onChange,
  placeholder,
  onAddNew,
  addNewLabel = "Add New",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedIds = useMemo(() => new Set(values.map((v) => v.id)), [values]);

  const suggestions = useMemo(() => {
    const q = normalizeName(query);
    if (!q) return [];
    return (options ?? [])
      .filter((o) => !selectedIds.has(o.id) && normalizeName(o.name).includes(q))
      .slice(0, 8);
  }, [query, options, selectedIds]);

  const add = (item) => {
    if (!item || selectedIds.has(item.id)) return;
    onChange([...values, item]);
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const remove = (item) => onChange(values.filter((v) => v.id !== item.id));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Only accept an exact option (or a single remaining suggestion) — never free text
      const exact = (options ?? []).find(
        (o) => !selectedIds.has(o.id) && normalizeName(o.name) === normalizeName(query)
      );
      if (exact) add(exact);
      else if (suggestions.length === 1) add(suggestions[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <SavingWrap saving={saving}>
      <div className="ob-field" ref={wrapRef} style={{ position: "relative" }}>
        <label className="ob-label">{label}</label>
        <div
          className="ob-input"
          onClick={() => inputRef.current?.focus()}
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "6px",
            minHeight: "42px",
            height: "auto",
            cursor: "text",
          }}
        >
          {values.map((v) => (
            <span
              key={v.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "3px 8px 3px 10px",
                borderRadius: "999px",
                background: "rgba(34, 211, 238, 0.12)",
                border: "1px solid rgba(34, 211, 238, 0.35)",
                color: "#22d3ee",
                fontSize: "0.78rem",
                fontWeight: 600,
                maxWidth: "100%",
              }}
              title={v.name}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {v.name}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(v);
                }}
                aria-label={`Remove ${v.name}`}
                style={{
                  background: "none",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 1,
                  fontSize: "0.9rem",
                  opacity: 0.8,
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={values.length === 0 ? placeholder : ""}
            style={{
              flex: 1,
              minWidth: "90px",
              background: "transparent",
              border: "none",
              outline: "none",
              color: "inherit",
              font: "inherit",
              padding: 0,
            }}
          />
        </div>

        {open && query.trim() && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              maxHeight: "220px",
              overflowY: "auto",
              zIndex: 20,
            }}
          >
            {suggestions.length === 0 ? (
              <div style={{ padding: "10px 14px" }}>
                <div style={{ fontSize: "13px", color: "#6b7280" }}>
                  {onAddNew
                    ? "No matches — would you like to add a new subbie?"
                    : "No matches — only names from the list can be added."}
                </div>
                {onAddNew && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const q = query;
                      setOpen(false);
                      setQuery("");
                      onAddNew(q);
                    }}
                    style={{
                      marginTop: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "999px",
                      border: "1px solid rgba(34, 197, 94, 0.45)",
                      background: "rgba(34, 197, 94, 0.12)",
                      color: "#15803d",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                    </svg>
                    {addNewLabel}
                  </button>
                )}
              </div>
            ) : (
              suggestions.map((s) => (
                <div
                  key={s.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(s)}
                  style={{ padding: "9px 14px", cursor: "pointer", fontSize: "14px", color: "#111827" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {s.name}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </SavingWrap>
  );
};

/* ============================================================================
 * Main component
 * ==========================================================================*/

const SubcontractorComplete = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false); // ↻ soft refresh
  const [loadError, setLoadError] = useState(null);
  const [records, setRecords] = useState([]);
  const [options, setOptions] = useState({ subbies: [], sites: [], positions: [] });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [addSubbie, setAddSubbie] = useState(null); // { recordId, query } while Add New Subbie is open
  const [editorOpen, setEditorOpen] = useState(false); // Subbie Editor modal
  const [activatedOpen, setActivatedOpen] = useState(false); // Activated modal
  const [busyFields, setBusyFields] = useState(new Set());
  const [toast, setToast] = useState(null);

  const searchWrapperRef = useRef(null);

  /* ---------- stylesheet ---------- */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = personalStyles;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  /* ---------- toast auto-dismiss ---------- */
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  /* ---------- fetch: records + autocomplete options ---------- */
  const loadAll = async () => {
    const [recRes, optRes] = await Promise.all([fetchFinalSubbies(), fetchFinalSubbieOptions()]);
    if (recRes.status !== "success") {
      throw new Error(recRes.error || "Failed to load subcontractors");
    }
    setRecords(recRes.data ?? []);
    if (optRes.status === "success") {
      setOptions({
        subbies: optRes.subbies ?? [],
        sites: optRes.sites ?? [],
        positions: optRes.positions?.length ? optRes.positions : POSITION_OPTIONS_FALLBACK,
      });
    } else {
      setOptions((o) => ({ ...o, positions: o.positions?.length ? o.positions : POSITION_OPTIONS_FALLBACK }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadAll();
      } catch (err) {
        setLoadError(err.message || "Failed to load subcontractors");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* soft refresh — refetch in place, keep the selected record + search
     (same as TestNTagDashboard's ↻) */
  const refresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try {
      await loadAll();
      setLoadError(null);
      setToast({ type: "success", message: "Subcontractors refreshed." });
    } catch (err) {
      setToast({ type: "error", message: err.message || "Refresh failed." });
    } finally {
      setRefreshing(false);
    }
  };

  /* ---------- close suggestions on outside click ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- derived ---------- */
  const uniqueNames = useMemo(() => {
    const seen = new Map();
    records.forEach((r) => {
      if (!r.name) return;
      const norm = normalizeName(r.name);
      if (!seen.has(norm)) seen.set(norm, r.name);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [records]);

  const suggestions = useMemo(() => {
    const q = normalizeName(searchQuery);
    if (!q) return [];
    return uniqueNames.filter((n) => normalizeName(n).includes(q)).slice(0, 8);
  }, [searchQuery, uniqueNames]);

  const activeRecord = useMemo(
    () => (selectedId ? records.find((r) => r.recordId === selectedId) ?? null : null),
    [records, selectedId]
  );

  // Active Clients are written by name (typecast), so id === name here
  const siteOptions = useMemo(
    () => (options.sites ?? []).map((n) => ({ id: n, name: n })),
    [options.sites]
  );

  const refreshOptions = async () => {
    const res = await fetchFinalSubbieOptions();
    if (res.status === "success") {
      setOptions((o) => ({
        ...o,
        subbies: res.subbies ?? o.subbies,
        sites: res.sites ?? o.sites,
        positions: res.positions?.length ? res.positions : o.positions,
      }));
    }
  };

  /* Merge a server response into state. Besides the edited record, the
   * partner-table sync can rename a subbie (its Subbie text is rebuilt) or
   * delete one (last cleaner removed) — propagate that to every record. */
  const applyServerData = (recordId, data) => {
    if (!data) return;
    const renamed = new Map((data.subbie ?? []).map((s) => [s.id, s.name]));
    const deleted = new Set(data.partnerSync?.deleted ?? []);
    setRecords((prev) =>
      prev.map((r) => {
        if (r.recordId === recordId) return { ...r, ...data };
        if (renamed.size === 0 && deleted.size === 0) return r;
        const subbie = (r.subbie ?? [])
          .filter((s) => !deleted.has(s.id))
          .map((s) => (renamed.has(s.id) ? { ...s, name: renamed.get(s.id) } : s));
        return { ...r, subbie };
      })
    );
  };

  // Red badge = records that need amendment (Subbie_Count = 1)
  const amendTotal = useMemo(
    () => records.filter((r) => onboardingBucket(r).key === "amend").length,
    [records]
  );

  /* ---------- selection ---------- */
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setShowSuggestions(true);
    if (activeRecord && normalizeName(val) !== normalizeName(activeRecord.name)) {
      setSelectedId(null);
    }
  };

  const handleSelectSuggestion = (name) => {
    const target = normalizeName(name);
    const rec = records.find((r) => normalizeName(r.name) === target);
    setSearchQuery(name);
    setSelectedId(rec?.recordId ?? null);
    setShowSuggestions(false);
  };

  const handleSelectFromModal = (record) => {
    setModalOpen(false);
    setSearchQuery(record.name || "");
    setSelectedId(record.recordId);
    setShowSuggestions(false);
  };

  /* ---------- auto-save ---------- */
  const isBusy = (recordId, field) => busyFields.has(`${recordId}:${field}`);

  const markBusy = (key, on) =>
    setBusyFields((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const handleFieldSave = async (record, field, value) => {
    const key = `${record.recordId}:${field}`;
    if (busyFields.has(key)) return;

    // Normalise to the shape the record stores, BEFORE the optimistic update:
    //   subbie        -> [{ id, name }]   (as the autocomplete gives it)
    //   activeClients -> ["Site A", ...]  (names only — the card maps them back
    //                                      to { id, name } for the autocomplete)
    const stored =
      field === "activeClients" ? (value ?? []).map((v) => (typeof v === "string" ? v : v.name)) : value;
    if (sameValue(record[field], stored)) return;

    const oldValue = record[field];

    setRecords((prev) =>
      prev.map((r) => (r.recordId === record.recordId ? { ...r, [field]: stored } : r))
    );
    markBusy(key, true);

    // Linked fields: Subbie is written by record id, Active Clients by name
    const payload =
      field === "subbie"
        ? stored.map((v) => v.id)
        : field === "activeClients"
        ? stored
        : stored;

    try {
      const res = await updateFinalSubbie(record.recordId, { [field]: payload });
      if (res.status === "success") {
        // Server returns the refreshed record (subbie names after the partner
        // sync, recalculated subbieCount / onboardingStatus, sync outcome)
        applyServerData(record.recordId, res.data);
        const deletedCount = res.data?.partnerSync?.deleted?.length ?? 0;
        const failed = res.data?.partnerSync?.errors ?? [];
        if (failed.length) {
          setToast({
            type: "error",
            message: `${labelFor(field)} saved, but the Subbie table sync failed: ${failed[0]}`,
          });
        } else {
          setToast({
            type: "success",
            message:
              deletedCount > 0
                ? `${labelFor(field)} has been saved — ${deletedCount} empty Subbie record${
                    deletedCount === 1 ? "" : "s"
                  } deleted.`
                : `${labelFor(field)} has been saved.`,
          });
        }
        if (field === "subbie") refreshOptions();
      } else {
        throw new Error(res.error || "Update failed");
      }
    } catch (err) {
      console.error("Update failed:", err);
      setRecords((prev) =>
        prev.map((r) => (r.recordId === record.recordId ? { ...r, [field]: oldValue } : r))
      );
      setToast({
        type: "error",
        message: err.message || `Failed to save ${labelFor(field)}. Please try again.`,
      });
    } finally {
      markBusy(key, false);
    }
  };

  // Subbie Editor saved a rename — reflect it on every record + the autocomplete
  const handleSubbieRenamed = (data) => {
    if (!data?.id) return;
    setRecords((prev) =>
      prev.map((r) => ({
        ...r,
        subbie: (r.subbie ?? []).map((s) => (s.id === data.id ? { ...s, name: data.name } : s)),
      }))
    );
    setOptions((o) => ({
      ...o,
      subbies: (o.subbies ?? [])
        .map((s) => (s.id === data.id ? { ...s, name: data.name } : s))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }));
    setToast({ type: "success", message: `Subbie updated to "${data.name}".` });
  };

  const handleSubbieCreated = (data) => {
    const recordId = addSubbie?.recordId;
    setAddSubbie(null);
    if (!recordId || !data) return;
    applyServerData(recordId, data);
    if (data.created) {
      setOptions((o) => ({
        ...o,
        subbies: [...(o.subbies ?? []).filter((s) => s.id !== data.created.id), data.created].sort(
          (a, b) => a.name.localeCompare(b.name)
        ),
      }));
    }
    setToast({ type: "success", message: `Subbie "${data.created?.name ?? ""}" created and linked.` });
  };

  /* ---------- render ---------- */
  const bucket = activeRecord ? onboardingBucket(activeRecord) : null;

  return (
    <div className="ob-container" style={{ width: "100%", maxWidth: "100%" }}>
      {toast && (
        <div className={`ob-toast ${toast.type === "success" ? "ob-toast-success" : "ob-toast-error"}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === "success" ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
          {toast.message}
        </div>
      )}

      {modalOpen && (
        <SubcontractorModal
          records={records}
          onSelect={handleSelectFromModal}
          onClose={() => setModalOpen(false)}
        />
      )}

      <style>{`@keyframes sc-spin{to{transform:rotate(360deg)}}`}</style>

      {activatedOpen && <ActivatedModal onClose={() => setActivatedOpen(false)} />}

      {editorOpen && (
        <SubbieEditorModal
          records={records}
          onSaved={handleSubbieRenamed}
          onClose={() => setEditorOpen(false)}
        />
      )}

      {addSubbie && activeRecord && (
        <AddSubbieModal
          cleaner={activeRecord}
          initialSubbie={addSubbie.query}
          onCreated={handleSubbieCreated}
          onClose={() => setAddSubbie(null)}
        />
      )}

      {loading && (
        <div className="ob-form-card">
          <div className="ob-info-banner">
            <LoadingState message={"Loading subcontractors…"} />
          </div>
        </div>
      )}

      {!loading && loadError && (
        <div className="ob-form-card">
          <div className="ob-info-banner ob-info-banner-warning">
            <ErrorIcon /> {loadError}
          </div>
        </div>
      )}

      {!loading && !loadError && (
        <>
          {/* ================= Search card ================= */}
          <div
            className="ob-form-card"
            style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
          >
            <div className="ob-record-card">
              <div className="ob-record-header">
                <div className="ob-record-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                    />
                  </svg>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <h3 className="ob-record-title" style={{ margin: 0 }}>
                    Subcontractor Complete
                  </h3>
                  <HelpButton
                    helpData={[
                      {
                        chip: "subcontractorcomplete",
                        urlFilter: "/static/pdffilters/SubcontractorComplete.pdf",
                      },
                    ]}
                  />
                  <IconButton
                    onClick={() => setModalOpen(true)}
                    alertCount={amendTotal}
                    title="Subcontractor Final Onboarding"
                    icon={<UsersPlusIcon />}
                  />
                  <IconButton
                    onClick={() => setEditorOpen(true)}
                    alertCount={0}
                    title="Subbie Editor"
                    icon={<UsersEditIcon />}
                  />
                  <IconButton
                    onClick={() => setActivatedOpen(true)}
                    alertCount={0}
                    title="Activated"
                    icon={<UserCheckIcon />}
                  />
                  <IconButton
                    onClick={refresh}
                    alertCount={0}
                    title="Refresh data — keeps the selected record"
                    icon={refreshing ? <Spin /> : <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>↻</span>}
                  />
                </div>
              </div>
              <p className="ob-helper" style={{ marginTop: "8px" }}>
                Search for a subcontractor, or open Subcontractor Final Onboarding to work
                through the status lists. Subbie, Active Clients and Position save
                automatically when changed and keep the Subbie table's links in sync.
                Use Subbie Editor to rename a subbie or add / remove names in it.
              </p>
            </div>

            <div ref={searchWrapperRef} style={{ position: "relative", marginTop: "20px" }}>
              <SearchInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name..."
              />

              {showSuggestions && suggestions.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    listStyle: "none",
                    margin: 0,
                    padding: "4px 0",
                    maxHeight: "240px",
                    overflowY: "auto",
                    zIndex: 10,
                  }}
                >
                  {suggestions.map((name) => (
                    <li
                      key={name}
                      onClick={() => handleSelectSuggestion(name)}
                      style={{ padding: "10px 14px", cursor: "pointer", fontSize: "14px", color: "#111827" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}

              {showSuggestions && searchQuery.trim() && suggestions.length === 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    padding: "12px 14px",
                    fontSize: "14px",
                    color: "#6b7280",
                    zIndex: 10,
                  }}
                >
                  No matching subcontractors.
                </div>
              )}
            </div>
          </div>

          {/* ================= Record card ================= */}
          {activeRecord && (
            <div
              className="ob-form-card"
              style={{ marginTop: "20px", width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
            >
              {/* Status pill — which of the 4 onboarding lists this record sits in */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                <span
                  style={{
                    fontSize: "0.62rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "rgba(34, 211, 238, 0.7)",
                    fontWeight: 700,
                  }}
                >
                  Onboarding status
                </span>
                <span
                  style={{
                    display: "inline-block",
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: bucket.color,
                    border: `1px solid ${bucket.color}`,
                    background: "rgba(255,255,255,0.03)",
                  }}
                  title={bucket.hint}
                >
                  {bucket.label}
                </span>
              </div>

              {/*
                Photo | col 2 | col 3 | col 4 | col 5
                Row 1:  Subbie ─────────────────────────────
                Row 2:  Name ───────────  Mobile   ABN
                Row 3:  Email ──────────  First    Surname
                Row 4:  Active Clients  Position  DOB  Working
              */}
              <div
                style={{
                  marginTop: "12px",
                  background: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(34, 211, 238, 0.15)",
                  borderRadius: "12px",
                  padding: "14px",
                  display: "grid",
                  gridTemplateColumns: "auto repeat(4, minmax(0, 1fr))",
                  gridTemplateRows: "auto auto auto auto",
                  columnGap: "14px",
                  rowGap: "10px",
                  minWidth: 0,
                }}
              >
                {/* Photo — full left column, read-only */}
                <div style={{ gridColumn: "1 / 2", gridRow: "1 / 5" }}>
                  {activeRecord.photo ? (
                    <img
                      src={activeRecord.photo}
                      alt={activeRecord.name || "Subcontractor photo"}
                      style={{
                        width: "260px",
                        height: "100%",
                        minHeight: "150px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        border: "1px solid rgba(34, 211, 238, 0.25)",
                        display: "block",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "130px",
                        height: "100%",
                        minHeight: "150px",
                        borderRadius: "12px",
                        border: "1px dashed rgba(34, 211, 238, 0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(255,255,255,0.3)",
                        fontSize: "0.7rem",
                        fontStyle: "italic",
                      }}
                    >
                      No photo
                    </div>
                  )}
                </div>

                {/* Row 1 — Subbie (linked, autocomplete) */}
                <div style={{ gridColumn: "2 / -1", minWidth: 0 }}>
                  <LinkedAutocomplete
                    label={labelFor("subbie")}
                    values={activeRecord.subbie ?? []}
                    options={options.subbies}
                    saving={isBusy(activeRecord.recordId, "subbie")}
                    onChange={(vals) => handleFieldSave(activeRecord, "subbie", vals)}
                    placeholder="Type to search subbies…"
                    onAddNew={(query) => setAddSubbie({ recordId: activeRecord.recordId, query })}
                    addNewLabel="Add New Subbie"
                  />
                </div>

                {/* Row 2 — Name (50%) | Mobile (25%) | ABN (25%) */}
                <div style={{ gridColumn: "2 / 4", minWidth: 0 }}>
                  <ReadOnlyField label={labelFor("name")} value={activeRecord.name} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <ReadOnlyField label={labelFor("mobile")} value={activeRecord.mobile} />
                </div>
                <div style={{ minWidth: 0 }}>
                  {/* Label comes from Work Status ("ABN"), value from ABN Number */}
                  <ReadOnlyField
                    label={activeRecord.workStatus || "ABN"}
                    value={activeRecord.abnNumber}
                  />
                </div>

                {/* Row 3 — Email (50%) | First Name (25%) | Surname (25%) */}
                <div style={{ gridColumn: "2 / 4", minWidth: 0 }}>
                  <ReadOnlyField label={labelFor("email")} value={activeRecord.email} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <ReadOnlyField label={labelFor("firstName")} value={activeRecord.firstName} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <ReadOnlyField label={labelFor("surname")} value={activeRecord.surname} />
                </div>

                {/* Row 4 — Active Clients | Position | DOB | Working */}
                <div style={{ minWidth: 0 }}>
                  <LinkedAutocomplete
                    label={labelFor("activeClients")}
                    values={(activeRecord.activeClients ?? []).map((n) => ({ id: n, name: n }))}
                    options={siteOptions}
                    saving={isBusy(activeRecord.recordId, "activeClients")}
                    onChange={(vals) => handleFieldSave(activeRecord, "activeClients", vals)}
                    placeholder="Type to search clients…"
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <SavingWrap saving={isBusy(activeRecord.recordId, "position")}>
                    <CustomSelect
                      label={labelFor("position")}
                      value={activeRecord.position ?? ""}
                      options={options.positions}
                      onChange={(val) => handleFieldSave(activeRecord, "position", val)}
                    />
                  </SavingWrap>
                </div>
                <div style={{ minWidth: 0 }}>
                  <ReadOnlyField
                    label={labelFor("dateOfBirth")}
                    value={toDisplayDate(activeRecord.dateOfBirth)}
                  />
                </div>
                <div style={{ minWidth: 0 }}>
                  <ReadOnlyField label={labelFor("working")} value={activeRecord.working} />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubcontractorComplete;